import React, { useState, useEffect } from 'react';
import { Button, Flex, Spinner, Panel } from '@maxhub/max-ui';
import { EventCard } from '../components/EventCard';
import { ConsentModal } from '../components/ConsentModal';
import { CalendarView } from '../components/CalendarView';
import { api } from '../api/client';
import { Event } from '../types';

interface GuestData {
  guestName?: string;
  existingGuestId?: number;
}

export const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [allEvents, setAllEvents] = useState<Event[]>([]); // Для календаря
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showConsent, setShowConsent] = useState(false);
  const [pendingEventId, setPendingEventId] = useState<number | null>(null);
  const [pendingGuestData, setPendingGuestData] = useState<GuestData | null>(null);
  const [userConsentGiven, setUserConsentGiven] = useState(false);

  const [showCalendar, setShowCalendar] = useState(false);

  // ============================================================
  //  ЗАГРУЗКА ДАННЫХ
  // ============================================================

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (err) {
      setError('Не удалось загрузить мероприятия');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllEvents = async () => {
    try {
      const response = await api.get('/events?includePast=true');
      setAllEvents(response.data);
      console.log('📅 Загружено мероприятий для календаря:', response.data.length);
    } catch (err) {
      console.error('❌ Ошибка загрузки всех мероприятий:', err);
    }
  };

  const checkUserConsent = async () => {
    try {
      const response = await api.get('/me');
      setUserConsentGiven(response.data.consent_given || false);
    } catch (err) {
      console.error('❌ Ошибка проверки согласия:', err);
    }
  };

  useEffect(() => {
    loadEvents();
    loadAllEvents();
    checkUserConsent();
  }, []);

  // ============================================================
  //  ЗАПИСЬ
  // ============================================================

  const handleRegister = async (eventId: number, guestName?: string, existingGuestId?: number) => {
    try {
      const payload: any = { eventId };
      if (guestName) {
        payload.guestName = guestName;
      }
      if (existingGuestId) {
        payload.existingGuestId = existingGuestId;
      }

      console.log('📤 Отправка запроса:', payload);

      const response = await api.post('/book', payload);

      // Обновляем оба списка
      await loadEvents();
      await loadAllEvents();

      // Показываем пользователю результат
      if (response.data.waitlist) {
        alert(response.data.message || '✅ Вы добавлены в лист ожидания.');
      } else {
        alert('✅ Вы успешно записались на мероприятие!');
      }
    } catch (err: any) {
      const message = err.response?.data?.error || 'Не удалось записаться';
      alert(`❌ ${message}`);
      console.error(err);
    }
  };

  const handleRegisterClick = (eventId: number, guestName?: string, existingGuestId?: number) => {
    if (userConsentGiven) {
      handleRegister(eventId, guestName, existingGuestId);
      return;
    }

    setPendingEventId(eventId);
    setPendingGuestData({ guestName, existingGuestId });
    setShowConsent(true);
  };

  const handleConsentConfirm = async () => {
    setShowConsent(false);

    try {
      await api.post('/consent', { consent: true });
      setUserConsentGiven(true);
    } catch (err) {
      console.error('❌ Ошибка сохранения согласия:', err);
      alert('Не удалось сохранить согласие. Попробуйте ещё раз.');
      setPendingEventId(null);
      setPendingGuestData(null);
      return;
    }

    if (pendingEventId) {
      const { guestName, existingGuestId } = pendingGuestData || {};
      await handleRegister(pendingEventId, guestName, existingGuestId);
      setPendingEventId(null);
      setPendingGuestData(null);
    }
  };

  const handleConsentCancel = () => {
    setShowConsent(false);
    setPendingEventId(null);
    setPendingGuestData(null);
  };

  // ============================================================
  //  ОБРАБОТКА ВЫБОРА ИЗ КАЛЕНДАРЯ
  // ============================================================

  const handleSelectEventFromCalendar = (eventId: number) => {
    const element = document.getElementById(`event-${eventId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.style.transition = 'box-shadow 0.3s';
      element.style.boxShadow = '0 0 20px rgba(0,122,255,0.5)';
      setTimeout(() => {
        element.style.boxShadow = '';
      }, 2000);
    }
  };

  // ============================================================
  //  ОТОБРАЖЕНИЕ
  // ============================================================

  if (loading) {
    return (
      <Flex style={{ justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <Spinner size={40} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex style={{ justifyContent: 'center', alignItems: 'center', padding: 40, flexDirection: 'column' }}>
        <p style={{ fontSize: 14 }}>{error}</p>
        <Button onClick={loadEvents} style={{ marginTop: 16 }}>
          Попробовать снова
        </Button>
      </Flex>
    );
  }

  return (
    <Panel style={{ padding: 16 }}>
      {/* Заголовок с кнопкой календаря */}
      <Flex style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>📋 Расписание</h1>
        <button
          onClick={() => setShowCalendar(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007AFF',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
          title="Показать календарь"
        >
          📅 Календарь
        </button>
      </Flex>

      {events.length === 0 ? (
        <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>
          Пока нет доступных мероприятий
        </p>
      ) : (
        events.map(event => (
          <div key={event.id} id={`event-${event.id}`}>
            <EventCard
              event={event}
              onRegister={handleRegisterClick}
            />
          </div>
        ))
      )}

      {/* Календарь */}
      <CalendarView
        isOpen={showCalendar}
        events={allEvents}
        onClose={() => setShowCalendar(false)}
        onSelectEvent={handleSelectEventFromCalendar}
      />

      <ConsentModal
        isOpen={showConsent}
        onConfirm={handleConsentConfirm}
        onCancel={handleConsentCancel}
      />
    </Panel>
  );
};